import { forgotPassword, getSmtpConfigured } from 'api-client/accounts';
import axios from 'axios';
import { ODSAlert, ODSButton, ODSFormGroup, ODSSpinner } from 'bc-ods';
import ValidatedFormikField from 'components/Utils/Forms/ValidatedFormikField';
import { Form, Formik } from 'formik';
import type { FC, ReactElement } from 'react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import yup from 'utils/yup';
import {
  FormStepEnum,
  SmartLoginActionsContext,
  SmartLoginStateContext,
} from './context/SmartLoginContextProvider';

const messages = defineMessages({
  resetButton: {
    id: 'ForgotForm.resetButton',
    defaultMessage: 'Reset password',
  },
  goBackButton: {
    id: 'ForgotForm.goBackButton',
    defaultMessage: 'Return to sign in',
  },
  usernameLabel: {
    id: 'ForgotForm.usernameLabel',
    defaultMessage: 'Confirm email or username',
  },
  usernamePlaceholder: {
    id: 'ForgotForm.usernamePlaceholder',
    defaultMessage: 'e.g. dave@navy.mil',
  },
  usernameFeedback: {
    id: 'ForgotForm.usernameFeedback',
    defaultMessage:
      '{error, select, min {Must be at least 3 characters long} noSpaces {No spaces allowed} other {Required}}',
  },
  unknownError: {
    id: 'ForgotForm.unknownError',
    defaultMessage: 'An unknown error occurred while processing your request. Please try again.',
  },
  buttonBack: {
    id: 'ForgotForm.buttonBack',
    defaultMessage: 'Back',
  },
  contactAdmin: {
    id: 'ForgotForm.contactAdmin',
    defaultMessage: 'Please contact your system administrator to reset your forgotten password.',
  },
});

type FormValues = {
  username: string;
};

function useHandleSubmit(setMessage, setShowError, intl) {
  const { setFormError } = useContext(SmartLoginActionsContext);

  const resetPassword = useCallback(
    async (username: string) => {
      try {
        const { data } = await forgotPassword(username);
        const { message } = data;
        setMessage(message);
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          setFormError(error?.response?.data?.error ?? intl.formatMessage(messages.unknownError));
        } else {
          setFormError(intl.formatMessage(messages.unknownError));
        }
        setShowError(true);
      }
    },
    [intl, setFormError, setMessage, setShowError]
  );

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      setShowError(false);
      await resetPassword(values.username);
    },
    [setShowError, resetPassword]
  );

  return handleSubmit;
}

function ForgotForm(): ReactElement {
  const intl = useIntl();
  const [message, setMessage] = useState();
  const [showError, setShowError] = useState(false);
  const { formStep, formError, name } = useContext(SmartLoginStateContext);
  const { goToPreviousStep } = useContext(SmartLoginActionsContext);

  const forgotFieldValidationSchema = yup.object().shape({
    username: yup.string().required('required').min(3, 'min').matches(/^\S*$/, 'noSpaces'),
  });

  const handleSubmit = useHandleSubmit(setMessage, setShowError, intl);

  return (
    <Formik<FormValues>
      initialValues={{ username: name ?? '' }}
      onSubmit={handleSubmit}
      validationSchema={forgotFieldValidationSchema}
      validateOnChange={false}
    >
      {({ errors, isSubmitting }) => (
        <div>
          {showError && (
            <ODSAlert color="danger" isOpen={showError}>
              {formError}
            </ODSAlert>
          )}
          {message ? (
            <div>
              <ODSAlert color="help" className="mb-3">
                {message}
              </ODSAlert>
              <ODSButton
                color="primary"
                className="w-100"
                onClick={() => {
                  goToPreviousStep();
                }}
                data-testid="cancel-forgot"
              >
                <FormattedMessage {...messages.goBackButton} values={{ step: formStep }} />
              </ODSButton>
            </div>
          ) : (
            <Form>
              <ODSFormGroup className="flex-grow-1">
                <ValidatedFormikField
                  name="username"
                  id="username"
                  label={<FormattedMessage {...messages.usernameLabel} />}
                  placeholder={intl.formatMessage(messages.usernamePlaceholder)}
                  autoFocus
                  data-testid="username"
                  autoComplete="username"
                  fast={true}
                  errorMessage={
                    <FormattedMessage
                      {...messages.usernameFeedback}
                      values={{ error: errors.username }}
                    />
                  }
                />
              </ODSFormGroup>
              <div className="d-flex gap-2">
                <ODSButton
                  className="flex-shrink-0"
                  quiet
                  onClick={() => {
                    goToPreviousStep();
                  }}
                  data-testid="cancel-forgot"
                >
                  <FormattedMessage {...messages.buttonBack} />
                </ODSButton>
                <ODSButton
                  color="secondary"
                  className="flex-grow-1"
                  disabled={isSubmitting ?? formStep === FormStepEnum.Login}
                  type="submit"
                  data-testid="submit"
                >
                  <FormattedMessage {...messages.resetButton} values={{ step: formStep }} />
                </ODSButton>
              </div>
            </Form>
          )}
        </div>
      )}
    </Formik>
  );
}

const ContactYourAdminScreen: FC = () => {
  const { goToPreviousStep } = useContext(SmartLoginActionsContext);
  return (
    <div className="w-100">
      <ODSAlert color="warning" className="mb-3">
        <FormattedMessage {...messages.contactAdmin} />
      </ODSAlert>
      <ODSButton
        color="primary"
        fullWidth
        onClick={() => {
          goToPreviousStep();
        }}
        data-testid="cancel-forgot"
      >
        <FormattedMessage {...messages.goBackButton} />
      </ODSButton>
    </div>
  );
};

const ForgotPassword: FC = () => {
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    void (async function load() {
      setLoading(true);
      const resp = await getSmtpConfigured();

      setSmtpConfigured(resp.data);
      setLoading(false);
    })();
  }, []);

  if (loading) {
    return <ODSSpinner className="pb-5 px-4" />;
  }

  if (!smtpConfigured) {
    return <ContactYourAdminScreen />;
  }
  return <ForgotForm />;
};

export default ForgotPassword;
